/** In-app paywall. `?mode=change` is opened from Membership. */
import { Ionicons } from '@expo/vector-icons';
import { useLocalSearchParams, useRouter, useFocusEffect } from 'expo-router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Animated, Linking, ScrollView, View } from 'react-native';
import type { PurchasesPackage, PurchasesStoreProduct } from 'react-native-purchases';
import { Button, XStack, YStack } from 'tamagui';
import { PostPurchaseSuccess } from '@/components/subscription/PostPurchaseSuccess';
import Container from '@/components/ui/Container';
import { BodyText, CaptionText, CardTitle, PageTitle } from '@/components/ui/Typography';
import { useAuth } from '@/lib/AuthContext';
import { BRAND_COLORS as C } from '@/lib/brandColors';
import { URLS } from '@/lib/constants';
import { readHomeSurveyPrefillFromMetadata } from '@/lib/dashboardHomeSurvey';
import {
  buildPendingPlanChange,
  clearPendingPlanChange,
  resolvePendingPlanChange,
  savePendingPlanChange,
} from '@/lib/pendingPlanChange';
import {
  getActiveMembershipEntitlement,
  isPurchasesAvailable,
  packagesFromOffering,
  refreshCustomerInfo,
} from '@/lib/purchases/RevenueCat';
import {
  describeAppleSubscription,
  formatDateTime,
  productDisplayName,
  type AppleSubscription,
} from '@/lib/purchases/subscriptionStatus';
import { useCommunityWebView } from '@/lib/CommunityWebViewContext';
import { usePurchases } from '@/lib/purchases/usePurchases';

const SUCCESS = '#00B176';

/** "14 days free" / "1 month free" from a StoreKit intro-offer period. */
function trialLabel(intro: PurchasesStoreProduct['introPrice']): string | null {
  if (!intro || intro.price !== 0) return null;
  const n = intro.periodNumberOfUnits;
  switch (intro.periodUnit) {
    case 'DAY':
      return `${n} day${n === 1 ? '' : 's'} free`;
    case 'WEEK':
      return `${n * 7} days free`;
    case 'MONTH':
      return `${n} month${n === 1 ? '' : 's'} free`;
    case 'YEAR':
      return `${n} year${n === 1 ? '' : 's'} free`;
    default:
      return 'Free trial';
  }
}

/**
 * "2 months free" for the annual card, derived from the sibling monthly
 * price so it stays correct if prices change. Null when it can't be computed
 * (no monthly package on the offering) or there's no saving to advertise.
 */
function annualSavingsLabel(
  packages: PurchasesPackage[],
  annualPrice: number,
): string | null {
  const monthly = packages.find(
    (x) =>
      x.packageType === 'MONTHLY' ||
      x.product.identifier.toLowerCase().includes('monthly'),
  );
  const monthlyPrice = monthly?.product.price;
  if (!monthlyPrice || monthlyPrice <= 0 || !annualPrice) return null;
  const monthsFree = Math.round(12 - annualPrice / monthlyPrice);
  if (monthsFree < 1) return null;
  return `${monthsFree} month${monthsFree === 1 ? '' : 's'} free`;
}

export default function UpgradeScreen() {
  const router = useRouter();
  const { mode, returnTo } = useLocalSearchParams<{ mode?: string; returnTo?: string }>();
  const changingPlan = mode === 'change';
  // Pro upgrade for existing Apple-billed members: renders the 'pro' offering.
  // Buying is a same-subscription-group upgrade, so Apple replaces the normal
  // sub with proration — never a second charge.
  const proUpgrade = mode === 'pro';
  const { offering, trialEligibility, loading, purchasing, error, buy, restore, isMember, reload } =
    usePurchases(proUpgrade ? 'pro' : undefined);
  const communityWebView = useCommunityWebView();
  const { preferenceMetadata, user } = useAuth();
  const [selected, setSelected] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState(false);
  const [currentProductId, setCurrentProductId] = useState<string | null>(null);
  const [purchased, setPurchased] = useState<AppleSubscription | null>(null);
  const [purchasedProductId, setPurchasedProductId] = useState<string | null>(null);
  const [requestedProductId, setRequestedProductId] = useState<string | null>(null);
  const [scheduledProductId, setScheduledProductId] = useState<string | null>(null);
  const [planResolved, setPlanResolved] = useState(false);

  // expo-router can REUSE a mounted /upgrade instance when navigating here
  // again with different params (seen in prod: the Hang's pro-upgrade push
  // landed on the still-mounted screen from an earlier purchase, whose stale
  // `succeeded` state skipped the paywall entirely). Entering with different
  // params is a new flow — reset the purchase-flow state. Done during render
  // (React's derived-state pattern) so the stale success screen never paints,
  // and only when the (mode, returnTo) pair changes, so a purchase completed
  // within one flow is never cleared.
  const paramsKey = `${mode ?? ''}|${returnTo ?? ''}`;
  const [prevParamsKey, setPrevParamsKey] = useState(paramsKey);
  if (paramsKey !== prevParamsKey) {
    setPrevParamsKey(paramsKey);
    setSucceeded(false);
    setPurchased(null);
    setPurchasedProductId(null);
    setRequestedProductId(null);
    setSelected(null);
  }

  const goBack = () => (router.canGoBack() ? router.back() : router.replace('/courses'));
  const goToCourses = () => router.replace('/courses' as never);

  useFocusEffect(
    useCallback(() => {
      if (!changingPlan) return;
      setSucceeded(false);
      setPurchased(null);
      setPurchasedProductId(null);
      setRequestedProductId(null);
      setSelected(null);
      setPlanResolved(false);

      let alive = true;
      void (async () => {
        await reload();
        const info = await refreshCustomerInfo();
        if (!alive) return;
        setCurrentProductId(getActiveMembershipEntitlement(info)?.productIdentifier ?? null);
        const pending = await resolvePendingPlanChange(user?.id, info);
        if (!alive) return;
        setScheduledProductId(pending?.newProductId ?? null);
        setPlanResolved(true);
      })();
      return () => {
        alive = false;
      };
    }, [changingPlan, reload, user?.id]),
  );

  if (!isPurchasesAvailable()) {
    return (
      <Screen>
        <PageTitle color={C.text} ta="center">Upgrade your membership</PageTitle>
        <BodyText color={C.muted} ta="center" mt="$3" mb="$6">
          Unlock every course, live sessions, and the full community.
        </BodyText>
        <PrimaryButton onPress={() => Linking.openURL(URLS.JOIN)}>
          View membership options
        </PrimaryButton>
        <Button chromeless color={C.muted} fontSize="$3" mt="$4" onPress={goBack}>Not now</Button>
      </Screen>
    );
  }

  if (isMember && !succeeded && !changingPlan && !proUpgrade) {
    return (
      <Screen>
        <SuccessMark />
        <PageTitle color={C.text} ta="center" mt="$4">You're all set</PageTitle>
        <BodyText color={C.muted} ta="center" mt="$3" mb="$6">
          Your membership is active. You have full access to every course.
        </BodyText>
        <PrimaryButton onPress={goBack}>Continue</PrimaryButton>
      </Screen>
    );
  }

  if (succeeded) {
    const planLabel =
      purchased?.planName ?? productDisplayName(purchasedProductId) ?? null;
    const changedAt = formatDateTime(new Date());
    const nextPayment = purchased?.periodEnd
      ? formatDateTime(purchased.periodEnd)
      : null;

    // Pro upgrade started from inside the Hang: after the success beat,
    // return the user to the exact Circle section they came from. Charset
    // check keeps the replayed path inside the autologin allowlist.
    const hangReturnPath =
      proUpgrade &&
      typeof returnTo === 'string' &&
      /^\/community(\/[A-Za-z0-9\-_/]*)?$/.test(returnTo)
        ? returnTo
        : null;
    if (proUpgrade && hangReturnPath) {
      return (
        <ProUpgradeSuccess
          planLabel={planLabel}
          onBackToHang={() => {
            goBack();
            communityWebView?.openCommunity(`${URLS.WORDPRESS_BASE}${hangReturnPath}`);
          }}
        />
      );
    }
    const askSurvey =
      !changingPlan &&
      !proUpgrade &&
      !readHomeSurveyPrefillFromMetadata(preferenceMetadata).instrument;
    const scheduledLabel =
      changingPlan &&
      requestedProductId &&
      purchased &&
      purchased.productId !== requestedProductId
        ? productDisplayName(requestedProductId)
        : null;

    const shared = { planLabel, nextPayment, purchased };

    if (askSurvey) {
      return (
        <PostPurchaseSuccess
          mode="onboarding"
          {...shared}
          onComplete={goToCourses}
          onSkip={goToCourses}
        />
      );
    }

    if (scheduledLabel && planLabel && nextPayment) {
      return (
        <PostPurchaseSuccess
          mode="thank-you"
          {...shared}
          title="Plan change scheduled"
          planSchedule={{
            currentPlan: planLabel,
            newPlan: scheduledLabel,
            switchDate: nextPayment,
          }}
          primaryLabel="Done"
          onPrimary={goBack}
        />
      );
    }

    if (changingPlan) {
      return (
        <PostPurchaseSuccess
          mode="thank-you"
          {...shared}
          title="Your plan is changed"
          effectiveAt={changedAt}
          planLabel={planLabel}
          billingHighlight={
            nextPayment
              ? {
                  prefix: purchased?.isTrial ? 'First payment' : 'Renews',
                  date: nextPayment,
                }
              : undefined
          }
          primaryLabel="Done"
          onPrimary={goBack}
        />
      );
    }

    return (
      <PostPurchaseSuccess
        mode="thank-you"
        {...shared}
        title="You're all set"
        message={
          planLabel
            ? `${planLabel} is active. Enjoy full access to every course and live session.`
            : 'Your membership is active. Enjoy full access to every course and live session.'
        }
        billingHighlight={
          nextPayment
            ? {
                prefix: purchased?.isTrial ? 'First payment' : 'Renews',
                date: nextPayment,
              }
            : undefined
        }
        primaryLabel="Start watching"
        onPrimary={goBack}
      />
    );
  }

  if (loading || (changingPlan && !planResolved)) {
    return (
      <Screen>
        <ActivityIndicator color={C.accent} size="large" />
      </Screen>
    );
  }

  const packages: PurchasesPackage[] = packagesFromOffering(offering);
  const switchablePackages = changingPlan
    ? packages.filter((p) => p.product.identifier !== currentProductId)
    : packages;

  if (packages.length === 0) {
    return (
      <Screen>
        <PageTitle color={C.text} ta="center">Memberships unavailable</PageTitle>
        <BodyText color={C.muted} ta="center" mt="$3" mb="$6">
          We couldn't load membership options right now. Please try again shortly.
        </BodyText>
        <PrimaryButton onPress={goBack}>Go back</PrimaryButton>
      </Screen>
    );
  }

  if (changingPlan && switchablePackages.length === 0) {
    return (
      <Screen>
        <PageTitle color={C.text} ta="center">No other plans to show</PageTitle>
        <BodyText color={C.muted} ta="center" mt="$3" mb="$6">
          We couldn't find another billing option for your membership tier. You can
          manage or cancel in your Apple ID subscription settings.
        </BodyText>
        <PrimaryButton onPress={goBack}>Go back</PrimaryButton>
      </Screen>
    );
  }

  const defaultId =
    changingPlan && currentProductId
      ? (switchablePackages.find((p) => p.product.identifier !== scheduledProductId)
          ?.identifier ??
        switchablePackages[0]?.identifier ??
        packages[0]?.identifier)
      : packages[0]?.identifier;
  const activeId = selected ?? defaultId ?? null;
  const activePkg = packages.find((p) => p.identifier === activeId) ?? packages[0];
  const selectingCurrent =
    !!changingPlan &&
    !!currentProductId &&
    activePkg?.product.identifier === currentProductId;
  const selectingScheduled =
    !!changingPlan &&
    !!scheduledProductId &&
    activePkg?.product.identifier === scheduledProductId;
  const activeHasTrial =
    !!activePkg &&
    !changingPlan &&
    !!trialEligibility[activePkg.product.identifier] &&
    !!trialLabel(activePkg.product.introPrice);

  const onSubscribe = async () => {
    if (!activePkg || selectingCurrent || selectingScheduled) return;
    const res = await buy(activePkg);
    if (!res.ok) return;
    const fresh = res.customerInfo ?? null;
    const requestedId = activePkg.product.identifier;
    const purchasedSub = describeAppleSubscription(fresh);
    const activeId =
      getActiveMembershipEntitlement(fresh)?.productIdentifier ?? requestedId;

    setRequestedProductId(requestedId);
    setPurchased(purchasedSub);
    setPurchasedProductId(activeId);
    setSucceeded(true);

    const userId = user?.id;
    if (!userId || !changingPlan) return;

    if (activeId !== requestedId && purchasedSub?.periodEnd) {
      await savePendingPlanChange(
        userId,
        buildPendingPlanChange(activeId, requestedId, purchasedSub.periodEnd),
      );
    } else {
      await clearPendingPlanChange(userId);
    }
  };

  return (
    <YStack f={1} bg={C.pageBg}>
      <ScrollView contentContainerStyle={{ flexGrow: 1, paddingVertical: 32 }}>
        <Container maxWidth={520} centered>
          <XStack jc="flex-end">
            <Button chromeless circular icon={<Ionicons name="close" size={24} color={C.muted} />}
              onPress={goBack} disabled={purchasing} />
          </XStack>

          <PageTitle color={C.text} ta="center" mt="$2">
            {proUpgrade
              ? 'Upgrade to Pro'
              : changingPlan
                ? 'Change your plan'
                : 'Unlock everything'}
          </PageTitle>
          <BodyText color={C.muted} ta="center" mt="$3" mb="$6">
            {proUpgrade
              ? 'Pro-only live sessions, mentor sessions, and more. Apple upgrades your current plan immediately, with proration.'
              : changingPlan
                ? 'Pick a different plan. Apple handles upgrades immediately; some switches apply at the next renewal.'
                : 'Every course, live sessions, and the full community. One membership.'}
          </BodyText>

          <YStack gap="$3">
            {packages.map((pkg) => {
              const active = pkg.identifier === activeId;
              const p = pkg.product;
              const isCurrent = changingPlan && p.identifier === currentProductId;
              const isScheduled = changingPlan && p.identifier === scheduledProductId;
              const isAnnual =
                pkg.packageType === 'ANNUAL' ||
                p.identifier.toLowerCase().includes('annual');
              const savings = isAnnual ? annualSavingsLabel(packages, p.price) : null;
              const trial =
                !changingPlan && trialEligibility[p.identifier]
                  ? trialLabel(p.introPrice)
                  : null;
              return (
                <YStack
                  key={pkg.identifier}
                  onPress={() => setSelected(pkg.identifier)}
                  bg={C.cardBg}
                  br="$6"
                  p="$4"
                  borderWidth={2}
                  borderColor={active ? C.accent : C.border}
                  opacity={isCurrent || isScheduled ? 0.75 : 1}
                  pressStyle={{ opacity: 0.9 }}
                  cursor="pointer"
                >
                  <XStack jc="space-between" ai="center">
                    <YStack f={1} pr="$3">
                      <XStack ai="center" gap="$2" flexWrap="wrap">
                        <CardTitle color={C.text}>{p.title}</CardTitle>
                        {isAnnual && (
                          <XStack bg={C.pageBg} br="$10" px="$2.5" py="$1">
                            <CaptionText color={C.text} fontWeight="700">
                              ✨ Best Value
                            </CaptionText>
                          </XStack>
                        )}
                      </XStack>
                      {!!savings && (
                        <CaptionText color={C.muted} fontWeight="600" mt="$1">
                          {savings}
                        </CaptionText>
                      )}
                      {!!isCurrent && (
                        <CaptionText color={C.accent} fontWeight="700" mt="$1">
                          Current plan
                        </CaptionText>
                      )}
                      {!!isScheduled && (
                        <CaptionText color={C.muted} fontWeight="700" mt="$1">
                          Already scheduled
                        </CaptionText>
                      )}
                      {!!trial && (
                        <CaptionText color={C.accent} fontWeight="700" mt="$1">
                          {trial}, then {p.priceString}
                        </CaptionText>
                      )}
                      {!!p.description && (
                        <CaptionText color={C.muted} mt="$1">{p.description}</CaptionText>
                      )}
                    </YStack>
                    <CardTitle color={C.text}>{p.priceString}</CardTitle>
                  </XStack>
                </YStack>
              );
            })}
          </YStack>

          {!!error && <BodyText color={C.error} ta="center" mt="$4">{error}</BodyText>}
          {selectingCurrent && (
            <BodyText color={C.muted} ta="center" mt="$4">
              This is already your current plan. Pick another option to switch.
            </BodyText>
          )}
          {selectingScheduled && !selectingCurrent && (
            <BodyText color={C.muted} ta="center" mt="$4">
              You already switched to this plan. It starts at your next renewal.
            </BodyText>
          )}

          <YStack mt="$6" gap="$3">
            <PrimaryButton
              onPress={onSubscribe}
              loading={purchasing}
              disabled={selectingCurrent || selectingScheduled}
            >
              {proUpgrade
                ? 'Upgrade'
                : changingPlan
                  ? 'Confirm plan'
                  : activeHasTrial
                    ? 'Start free trial'
                    : 'Subscribe'}
            </PrimaryButton>
            {!changingPlan && !proUpgrade && (
              <Button chromeless color={C.muted} fontSize="$3" onPress={restore} disabled={purchasing}>
                Restore purchases
              </Button>
            )}
          </YStack>

          <CaptionText color={C.muted} ta="center" mt="$5" px="$2">
            Subscriptions auto-renew until cancelled. Payment is charged to your Apple ID and renews
            unless turned off at least 24 hours before the period ends. Manage or cancel in your Apple
            ID settings.
          </CaptionText>
          <XStack jc="center" gap="$4" mt="$3">
            <CaptionText color={C.accent} onPress={() => Linking.openURL(URLS.TERMS)} cursor="pointer">
              Terms of Use
            </CaptionText>
            <CaptionText color={C.accent} onPress={() => Linking.openURL(URLS.PRIVACY)} cursor="pointer">
              Privacy Policy
            </CaptionText>
          </XStack>
        </Container>
      </ScrollView>
    </YStack>
  );
}

/**
 * Success screen for a Pro upgrade started inside the Hang. Holds a short
 * "unlocking" beat (Circle's pro space grant arrives via the webhook a few
 * seconds after purchase) before offering the way back to the section the
 * user came from.
 */
function ProUpgradeSuccess({
  planLabel,
  onBackToHang,
}: {
  planLabel: string | null;
  onBackToHang: () => void;
}) {
  const [ready, setReady] = useState(false);
  const progress = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(progress, {
      toValue: 1,
      duration: 3000,
      useNativeDriver: false,
    }).start();
    const t = setTimeout(() => setReady(true), 3000);
    return () => clearTimeout(t);
  }, [progress]);

  return (
    <Screen>
      <SuccessMark />
      <PageTitle color={C.text} ta="center" mt="$4">
        Welcome to Pro
      </PageTitle>
      <BodyText color={C.muted} ta="center" mt="$3" mb="$6">
        {planLabel ?? 'Open Studio Pro'} is active. Enjoy Pro-only live
        sessions, mentor sessions, and more.
      </BodyText>
      {ready ? (
        <PrimaryButton onPress={onBackToHang}>Back to The Hang</PrimaryButton>
      ) : (
        <YStack w="100%" ai="center" gap="$3">
          <BodyText color={C.muted} ta="center">
            Your Pro areas are unlocking…
          </BodyText>
          <View
            style={{
              width: '70%',
              height: 6,
              borderRadius: 3,
              backgroundColor: C.border,
              overflow: 'hidden',
            }}
          >
            <Animated.View
              style={{
                height: '100%',
                backgroundColor: C.accent,
                width: progress.interpolate({
                  inputRange: [0, 1],
                  outputRange: ['0%', '100%'],
                }),
              }}
            />
          </View>
        </YStack>
      )}
    </Screen>
  );
}

function Screen({ children }: { children: React.ReactNode }) {
  return (
    <YStack f={1} bg={C.pageBg} ai="center" jc="center" px="$5">
      <Container maxWidth={480} centered>
        <YStack ai="center" jc="center">{children}</YStack>
      </Container>
    </YStack>
  );
}

function PrimaryButton({
  children,
  onPress,
  loading,
  disabled,
}: {
  children: React.ReactNode;
  onPress: () => void;
  loading?: boolean;
  disabled?: boolean;
}) {
  return (
    <Button onPress={onPress} disabled={loading || disabled} bg={C.accent} color={C.cardBg}
      fontWeight="700" fontSize="$4" br="$4" w="100%" opacity={disabled ? 0.5 : 1}
      pressStyle={{ opacity: 0.85 }} hoverStyle={{ opacity: 0.9 }}
      icon={loading ? <ActivityIndicator color={C.cardBg} /> : undefined}>
      {loading ? '' : children}
    </Button>
  );
}

function SuccessMark() {
  return (
    <YStack width={72} height={72} br={36} bg={C.cardBg} ai="center" jc="center"
      borderWidth={1} borderColor={C.border}>
      <Ionicons name="checkmark-circle" size={48} color={SUCCESS} />
    </YStack>
  );
}
